import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Autocomplete,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
    Paper,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    IconButton,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { 
    format, isValid, getHours, getMinutes, 
    addHours, isWithinInterval, isAfter, isBefore,
    isSameDay, parseISO
} from "date-fns";
import axios from "axios";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";
import AppToast from "../AppToast";

// Custom Time Picker Component
const CustomTimePicker = ({ value, onChange, label, disabled, bookedSlots = [], selectedEngineer, selectedDate }) => {
    const [hour, setHour] = useState(10);
    const [minute, setMinute] = useState(0);
    const [ampm, setAmpm] = useState('AM');

    useEffect(() => {
        if (value) {
            const date = new Date(value);
            const hours = date.getHours();
            const minutes = date.getMinutes();
            
            setHour(hours % 12 || 12);
            setMinute(minutes);
            setAmpm(hours >= 12 ? 'PM' : 'AM');
        }
    }, [value]);

    // Generate hour options (9 to 12 for AM, 1 to 7 for PM)
    const hourOptions = [];
    
    // Add AM hours (9 AM to 11 AM)
    for (let h = 9; h <= 11; h++) {
        hourOptions.push({ value: h, label: `${h}`, period: 'AM' });
    }
    hourOptions.push({ value: 12, label: '12', period: 'PM' }); // 12 PM (noon)
    
    // Add PM hours (1 PM to 7 PM)
    for (let h = 1; h <= 7; h++) {
        hourOptions.push({ value: h, label: `${h}`, period: 'PM' });
    }

    // Generate minute options (0-59)
    const generateMinuteOptions = () => {
        const options = [];
        for (let m = 0; m < 60; m++) {
            options.push({
                value: m,
                label: m.toString().padStart(2, '0')
            });
        }
        return options;
    };

    const minuteOptions = generateMinuteOptions();

    const handleHourChange = (e) => {
        const newHour = parseInt(e.target.value);
        setHour(newHour);
        
        // Update AM/PM based on selection
        const selectedOption = hourOptions.find(opt => opt.value === newHour);
        if (selectedOption) {
            setAmpm(selectedOption.period);
        }
        
        updateTime(newHour, minute, selectedOption?.period || ampm);
    };

    const handleMinuteChange = (e) => {
        const newMinute = parseInt(e.target.value) || 0;
        setMinute(newMinute);
        updateTime(hour, newMinute, ampm);
    };

    const updateTime = (h, m, a) => {
        let hour24 = h;
        if (a === 'AM' && h === 12) {
            hour24 = 0;
        } else if (a === 'PM' && h !== 12) {
            hour24 = h + 12;
        }

        const newDate = selectedDate ? new Date(selectedDate) : new Date();
        newDate.setHours(hour24, m, 0, 0);
        onChange(newDate);
    };

    // Calculate the actual and blocked times for display
    const calculateTimeSlots = () => {
        if (!value) return null;
        
        const selectedTime = new Date(value);
        
        // Actual visit: selected time to +1 hour
        const actualVisitStart = new Date(selectedTime);
        const actualVisitEnd = addHours(selectedTime, 1);
        
        // Blocked time: 1 hour before to 1 hour after actual visit (3 hours total)
        const blockedStart = addHours(selectedTime, -1);
        const blockedEnd = addHours(selectedTime, 2);
        
        return {
            actualVisit: {
                start: actualVisitStart,
                end: actualVisitEnd
            },
            blockedTime: {
                start: blockedStart,
                end: blockedEnd
            }
        };
    };

    // Check if the selected time conflicts with booked slots (using blocked time)
    const checkTimeConflict = () => {
        if (!selectedDate || !bookedSlots.length) return false;
        
        const timeSlots = calculateTimeSlots();
        if (!timeSlots) return false;
        
        const { blockedTime } = timeSlots;
        
        return bookedSlots.some(slot => {
            const slotStart = new Date(slot.startTime);
            const slotEnd = new Date(slot.endTime);
            
            // Check for overlap in blocked time
            return (
                (blockedTime.start >= slotStart && blockedTime.start < slotEnd) ||
                (blockedTime.end > slotStart && blockedTime.end <= slotEnd) ||
                (blockedTime.start <= slotStart && blockedTime.end >= slotEnd)
            );
        });
    };

    const hasConflict = checkTimeConflict();
    const timeSlots = calculateTimeSlots();

    return (
        <Box sx={{ maxHeight: '300px', overflow: 'hidden' }}>
            <Typography sx={{ 
                ...TYPOGRAPHY.BODY_LARGE, 
                color: COLORS.TEXT,
                mb: 1 
            }}>
                {label}
            </Typography>
            
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                flexWrap: 'wrap',
                mb: 2
            }}>
                {/* Hour Selection - with fixed height dropdown */}
                <Box sx={{ flex: 1, minWidth: 120 }}>
                    <TextField
                        select
                        label="Hour"
                        size="small"
                        value={hour}
                        onChange={handleHourChange}
                        SelectProps={{
                            native: true,
                            MenuProps: {
                                PaperProps: {
                                    sx: {
                                        maxHeight: 200,
                                        overflow: 'auto',
                                        '&::-webkit-scrollbar': {
                                            width: '6px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: COLORS.BACKGROUND,
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: COLORS.LIGHT_SECONDARY,
                                            borderRadius: '3px',
                                        }
                                    }
                                }
                            }
                        }}
                        fullWidth
                        error={hasConflict}
                        disabled={disabled}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    >
                        {hourOptions.map((option) => (
                            <option key={`${option.value}-${option.period}`} value={option.value}>
                                {option.label} {option.period}
                            </option>
                        ))}
                    </TextField>
                </Box>

                {/* Minute Selection - with fixed height dropdown */}
                <Box sx={{ flex: 1, minWidth: 120 }}>
                    <TextField
                        select
                        label="Minute"
                        size="small"
                        value={minute}
                        onChange={handleMinuteChange}
                        SelectProps={{
                            native: true,
                            MenuProps: {
                                PaperProps: {
                                    sx: {
                                        maxHeight: 200,
                                        overflow: 'auto',
                                        '&::-webkit-scrollbar': {
                                            width: '6px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: COLORS.BACKGROUND,
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: COLORS.LIGHT_SECONDARY,
                                            borderRadius: '3px',
                                        }
                                    }
                                }
                            }
                        }}
                        fullWidth
                        error={hasConflict}
                        disabled={disabled}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    >
                        {minuteOptions.map((min) => (
                            <option key={min.value} value={min.value}>
                                {min.label}
                            </option>
                        ))}
                    </TextField>
                </Box>
            </Box>
            
            {/* Time Slot Display - Fixed height container */}
            {timeSlots && (
                <Box sx={{ 
                    mt: 2,
                    p: 2, 
                    borderRadius: 2,
                    bgcolor: hasConflict ? `${COLORS.ERROR}10` : `${COLORS.SECONDARY}10`,
                    border: `1px solid ${hasConflict ? COLORS.ERROR : COLORS.LIGHT_SECONDARY}`,
                    maxHeight: '150px',
                    overflow: 'hidden'
                }}>
                    <Typography sx={{ 
                        ...TYPOGRAPHY.BODY_SMALL, 
                        color: hasConflict ? COLORS.ERROR : COLORS.SECONDARY,
                    
                        mb: 1
                    }}>
                        {hasConflict ? '⛔ Time Slot Conflict' : '📅 Time Allocation'}
                    </Typography>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 1,
                        maxHeight: '70px',
                        overflow: 'hidden'
                    }}>
                        {/* Actual Visit Time */}
                        <Box>
                            <Typography sx={{ 
                                ...TYPOGRAPHY.CAPTION, 
                                color: COLORS.TEXT,
                                mb: 0.5
                            }}>
                                Actual Visit Time:
                            </Typography>
                            <Typography sx={{ 
                                ...TYPOGRAPHY.BODY_SMALL, 
                                color: COLORS.PRIMARY,
                                fontWeight: 500,
                                lineHeight: 1.2
                            }}>
                                {format(timeSlots.actualVisit.start, 'hh:mm a')} - {format(timeSlots.actualVisit.end, 'hh:mm a')}
                            </Typography>
                        </Box>
                        
                        {/* Engineer Blocked Time */}
                        <Box>
                            <Typography sx={{ 
                                ...TYPOGRAPHY.CAPTION, 
                                color: COLORS.TEXT,
                                mb: 0.5
                            }}>
                                Engineer Blocked Time (incl. travel):
                            </Typography>
                            <Typography sx={{ 
                                ...TYPOGRAPHY.BODY_SMALL, 
                                color: COLORS.PRIMARY,
                                fontWeight: 500,
                                lineHeight: 1.2
                            }}>
                                {format(timeSlots.blockedTime.start, 'hh:mm a')} - {format(timeSlots.blockedTime.end, 'hh:mm a')}
                                <Typography component="span" sx={{ 
                                    ...TYPOGRAPHY.CAPTION, 
                                    color: COLORS.SECONDARY,
                                    ml: 1,
                                    display: 'inline-block'
                                }}>
                                    (3 hours total)
                                </Typography>
                            </Typography>
                        </Box>
                    </Box>
                    
                    {hasConflict && (
                        <Typography sx={{ 
                            ...TYPOGRAPHY.CAPTION, 
                            color: COLORS.ERROR,
                            mt: 0.5,
                            fontStyle: 'italic',
                            lineHeight: 1.2
                        }}>
                            This time conflicts with engineer's existing bookings
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

const AssignEngineerModal = ({ open, onClose, project, onSuccess }) => {
    const { token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [engineers, setEngineers] = useState([]);
    const [selectedEngineer, setSelectedEngineer] = useState(null);
    const [visitDate, setVisitDate] = useState(null);
    const [visitTime, setVisitTime] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [error, setError] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [bookedSlots, setBookedSlots] = useState([]);
    const [toast, setToast] = useState({ open: false, type: 'success', title: '', message: '' });

    // Debug: Track modal open/close
    useEffect(() => {
        console.log("AssignEngineerModal open state changed:", open);
        if (open) {
            console.log("AssignEngineerModal opened for project:", project?._id);
        } else {
            console.log("AssignEngineerModal closed");
        }
    }, [open, project]);

    // Get preferred date range from project
    const getPreferredDateRange = () => {
        if (!project?.preferredSiteVisitDate) return null;
        
        const fromDate = project.preferredSiteVisitDate.fromDate 
            ? new Date(project.preferredSiteVisitDate.fromDate)
            : null;
        const toDate = project.preferredSiteVisitDate.toDate 
            ? new Date(project.preferredSiteVisitDate.toDate)
            : null;
            
        return { fromDate, toDate };
    };

    // Check if date is within preferred range
    const isDateInPreferredRange = (date) => {
        const range = getPreferredDateRange();
        if (!range?.fromDate) return true; // If no range specified, allow any date
        
        const fromDate = new Date(range.fromDate);
        fromDate.setHours(0, 0, 0, 0);
        
        const toDate = range.toDate ? new Date(range.toDate) : fromDate;
        toDate.setHours(23, 59, 59, 999);
        
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        
        return checkDate >= fromDate && checkDate <= toDate;
    };

    const showToast = (type, title, message) => {
        setToast({ open: true, type, title, message });
    };
    
    // Close toast
    const handleCloseToast = () => {
        setToast(prev => ({ ...prev, open: false }));
    };

    // Get date validation function for DatePickerconst
    const shouldDisableDate = (date) => {
    // Don't allow dates before today (allow today)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    if (checkDate < today) return true;  // Only disable past dates
    
    // Check if within preferred range
    return !isDateInPreferredRange(date);
};

    // Fetch engineer's booked slots
    const fetchEngineerBookedSlots = useCallback(async (engineerId, date) => {
        if (!engineerId || !date) {
            setBookedSlots([]);
            return;
        }

        try {
            setLoadingSlots(true);
            
            const response = await axios.get(
                `${url}/api/admin/engineer-current-timeslot/${engineerId}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            let allBookedSlots = [];
            
            if (response.data?.data?.allBookings) {
                allBookedSlots = response.data.data.allBookings
                    .filter(booking => booking.status === 'BOOKED')
                    .map(booking => ({
                        startTime: new Date(booking.startTime),
                        endTime: new Date(booking.endTime),
                        projectName: booking.projectName || 'Unknown Project',
                        status: booking.status
                    }));
            }
            
            setBookedSlots(allBookedSlots);
        } catch (err) {
            console.error("Error fetching booked slots:", err);
            setBookedSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    }, [token]);

    // Initialize form when modal opens
    useEffect(() => {
        if (open && project) {
            setIsEditMode(project.status === "ASSIGNED" && project.assignedEngineer);
            
            if (project.assignedEngineer && typeof project.assignedEngineer === 'object') {
                setSelectedEngineer(project.assignedEngineer);
            }
            
            // Set default time to 10:00 AM
            const defaultTime = new Date();
            defaultTime.setHours(10, 0, 0, 0);
            setVisitTime(defaultTime);
            
            // Set default date to preferred from date or tomorrow
            const preferredRange = getPreferredDateRange();
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(0, 0, 0, 0);
            
            if (preferredRange?.fromDate && isDateInPreferredRange(preferredRange.fromDate)) {
                setVisitDate(preferredRange.fromDate);
            } else {
                setVisitDate(tomorrow);
            }
            
            // If existing assignment, populate date/time
            if (project.assignedVisitDate) {
                const existingDate = new Date(project.assignedVisitDate);
                setVisitDate(existingDate);
                setVisitTime(existingDate);
            }
        }
    }, [open, project]);

    // Handle engineer selection
    const handleEngineerSelect = (engineer) => {
        setSelectedEngineer(engineer);
        setVisitTime(null);
        
        if (engineer && visitDate) {
            fetchEngineerBookedSlots(engineer._id, visitDate);
        }
    };

    // Handle date change
// Handle date change
const handleDateChange = (newDate) => {
    if (!newDate) {
        setVisitDate(null);
        setVisitTime(null);
        setError(null); // Clear error when clearing date
        return;
    }
    
    // Clear any previous errors
    setError(null);
    
    // Check if date is within preferred range
    if (!isDateInPreferredRange(newDate)) {
        setError("Selected date is not within the preferred date range");
        setVisitDate(newDate); // Still set the date even if not in range
        return;
    }
    
    setVisitDate(newDate);
    
    // Reset time for new date selection
    // Set default time to 10:00 AM for the selected date
    const defaultTime = new Date(newDate);
    defaultTime.setHours(10, 0, 0, 0);
    
    // Check if we're selecting today's date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const selectedDate = new Date(newDate);
    selectedDate.setHours(0, 0, 0, 0);
    
    // If selecting today, check current time
    if (selectedDate.getTime() === today.getTime()) {
        const now = new Date();
        const currentHour = now.getHours();
        
        // If today and current time is past 10 AM, set to next available hour
        if (currentHour >= 10) {
            const nextHour = new Date();
            nextHour.setHours(currentHour + 1, 0, 0, 0);
            
            // Make sure we don't exceed 7 PM
            if (nextHour.getHours() < 19) {
                setVisitTime(nextHour);
            } else {
                setVisitTime(null);
                setError("Cannot schedule visits after 7:00 PM today");
            }
        } else {
            // Before 10 AM today, keep 10:00 AM default
            setVisitTime(defaultTime);
        }
    } else {
        // Future date, use 10:00 AM default
        setVisitTime(defaultTime);
    }
    
    // Fetch engineer's booked slots
    if (selectedEngineer) {
        fetchEngineerBookedSlots(selectedEngineer._id, newDate);
    }
};

    // Fetch approved engineers
    const fetchEngineers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${url}/api/users/fetch`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { 
                    role: "ENGINEER", 
                    status: "APPROVED",
                    search: searchTerm || undefined
                }
            });

            setEngineers(response.data?.users || []);
        } catch (err) {
            console.error("Error fetching engineers:", err);
            setError("Failed to load engineers");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchEngineers();
        }
    }, [open, searchTerm]);

    const handleAssign = async () => {
        setError(null);

        if (!selectedEngineer) {
            showToast('error', 'Selection Required', 'Please select an engineer');
            return;
        }

        if (!visitDate) {
            showToast('error', 'Date Required', 'Please select a visit date');
            return;
        }

        if (!visitTime) {
            showToast('error', 'Time Required', 'Please select a visit time');
            return;
        }

        try {
            setLoading(true);

            // Combine date and time
            const combinedDateTime = new Date(visitDate);
            const timeDate = new Date(visitTime);
            
            combinedDateTime.setHours(
                timeDate.getHours(),
                timeDate.getMinutes(),
                0,
                0
            );

            // Check if time is between 9 AM and 7 PM
            const visitHour = combinedDateTime.getHours();
            const visitMinute = combinedDateTime.getMinutes();

            // Calculate time in minutes for accurate comparison
            const visitTimeInMinutes = visitHour * 60 + visitMinute;
            const startOfWorkDay = 9 * 60; // 9:00 AM = 540 minutes
            const endOfWorkDay = 19 * 60; // 7:00 PM = 1140 minutes

            // Allow up to 7:00 PM exactly (19:00)
            if (visitTimeInMinutes < startOfWorkDay || visitTimeInMinutes > endOfWorkDay) {
                showToast('error', 'Invalid Time', 
                    `Visit time must be between 9:00 AM and 7:00 PM. Selected: ${format(combinedDateTime, 'hh:mm a')}`);
                return;
            }

            // Calculate actual visit end time
            const actualVisitEnd = addHours(combinedDateTime, 1);
            
            // Also ensure the visit ends by 8:00 PM (since it's 1 hour visit)
            const visitEndInMinutes = visitTimeInMinutes + 60;
            if (visitEndInMinutes > endOfWorkDay + 60) { // 8:00 PM (end of work day + 1 hour)
                showToast('error', 'Invalid Time', 
                    `Visit would end at ${format(actualVisitEnd, 'hh:mm a')}, which is outside working hours`);
                return;
            }

            // Check if time is in the future
            // if (combinedDateTime <= new Date()) {
            //     showToast('error', 'Invalid Time', 'Visit date and time must be in the future');
            //     return;
            // }

            // Calculate blocked time (1 hour before to 1 hour after visit)
            const actualVisitStart = new Date(combinedDateTime);
            const blockedStart = addHours(combinedDateTime, -1);
            const blockedEnd = addHours(combinedDateTime, 2);

            // Check for time conflicts using BLOCKED TIME
            const hasConflict = bookedSlots.some(slot => {
                const slotStart = new Date(slot.startTime);
                const slotEnd = new Date(slot.endTime);
                
                // Check for overlap in blocked time (3-hour window)
                return (
                    (blockedStart >= slotStart && blockedStart < slotEnd) ||
                    (blockedEnd > slotStart && blockedEnd <= slotEnd) ||
                    (blockedStart <= slotStart && blockedEnd >= slotEnd)
                );
            });

            if (hasConflict) {
                showToast('error', 'Time Conflict', 
                    `Engineer is blocked from ${format(blockedStart, 'hh:mm a')} to ${format(blockedEnd, 'hh:mm a')} due to existing booking`);
                return;
            }

            // Format date and time for backend
            const date = format(combinedDateTime, 'yyyy-MM-dd');
            const time = format(combinedDateTime, 'HH:mm:ss');
            
            const payload = {
                requestId: project._id,
                engineerId: selectedEngineer._id,
                date: date,
                time: time
            };

            console.log("Sending payload:", payload);
            console.log("Time slots:", {
                actualVisit: `${format(actualVisitStart, 'hh:mm a')} - ${format(actualVisitEnd, 'hh:mm a')}`,
                blockedTime: `${format(blockedStart, 'hh:mm a')} - ${format(blockedEnd, 'hh:mm a')}`,
                totalBlockedHours: 3
            });

            const response = await axios.post(
                `${url}/api/admin/assign-engineer`,
                payload,
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log("API Response:", response.data);

            // Show success toast
            showToast('success', 'Engineer Assigned', 
                `${selectedEngineer.name} assigned for visit at ${format(combinedDateTime, 'hh:mm a')}. Engineer blocked for 3 hours including travel time.`);

            if (onSuccess) {
                onSuccess(response.data.data);
            }
            
            // Close modal after delay to show toast
            setTimeout(() => {
                onClose();
            }, 2000);
            
        } catch (err) {
            console.error("Error assigning engineer:", err);
            const errorMsg = err.response?.data?.message || err.message || "Failed to assign engineer";
            showToast('error', 'Assignment Failed', errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleClearAssignment = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(
                `${url}/api/admin/remove-engineer`,
                {
                    requestId: project._id
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            console.log("Engineer removed:", response.data);

            showToast('success', 'Assignment Removed', 
                `Engineer assignment has been successfully removed from project.`);

            if (onSuccess) {
                onSuccess(response.data.data);
            }
            
            setTimeout(() => {
                onClose();
            }, 2000);
            
        } catch (err) {
            console.error("Error removing engineer:", err);
            const errorMsg = err.response?.data?.message || "Failed to remove engineer";
            showToast('error', 'Removal Failed', errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const formatPreferredDateRange = () => {
        const range = getPreferredDateRange();
        if (!range?.fromDate) return "Not specified";
        
        if (range.toDate) {
            return `${format(range.fromDate, 'MMM dd, yyyy')} - ${format(range.toDate, 'MMM dd, yyyy')}`;
        }
        return format(range.fromDate, 'MMM dd, yyyy');
    };

    // Helper text for date picker
    const getDatePickerHelperText = () => {
        if (!selectedEngineer) return "Select an engineer first";
        
        const range = getPreferredDateRange();
        if (!range?.fromDate) return "Select any date from tomorrow onwards";
        
        if (range.toDate) {
            return `Select date between ${format(range.fromDate, 'MMM dd')} and ${format(range.toDate, 'MMM dd, yyyy')}`;
        }
        return `Select date on or after ${format(range.fromDate, 'MMM dd, yyyy')}`;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        bgcolor: "#fff",
                        maxHeight: '90vh',
                        height: '90vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }
                }}
            >
                <DialogTitle sx={{ 
                    p: 3, 
                    pb: 2,
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Icon name="profile" color={COLORS.PRIMARY} size={24} />
                        <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
                            {isEditMode ? "Edit Engineer Assignment" : "Assign Engineer"}
                        </Typography>
                    </Box>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 0.5 }}>
                        {project?.nameOfProject}
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ 
                    p: 3, 
                    pt: 2,
                    flex: 1,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: COLORS.BACKGROUND,
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: COLORS.LIGHT_SECONDARY,
                        borderRadius: '4px',
                        '&:hover': {
                            background: COLORS.SECONDARY,
                        }
                    },
                    '-ms-overflow-style': 'none',
                    'scrollbar-width': 'thin',
                }}>
                
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                mb: 3,
                                borderRadius: 2,
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Project Information - Compact */}
                    <Box sx={{ 
                        mb: 3, 
                        p: 2, 
                        borderRadius: 2,
                        bgcolor: COLORS.BACKGROUND,
                        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        maxHeight: '120px',
                        overflow: 'hidden',
                        mt:3
                    }}>
                        <Typography sx={{ 
                            ...TYPOGRAPHY.BODY_LARGE, 
                            color: COLORS.PRIMARY,
                            mb: 1,
                            
                        }}>
                            Project Requirements
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Icon name="calendar3" color={COLORS.SECONDARY} size={16} />
                                <Typography sx={{ 
                                    ...TYPOGRAPHY.BODY_SMALL, 
                                    color: COLORS.PRIMARY,
                                    fontWeight: 500,
                                    lineHeight: 1.2
                                }}>
                                    {formatPreferredDateRange()}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Icon name="clock" color={COLORS.SECONDARY} size={16} />
                                <Typography sx={{ 
                                    ...TYPOGRAPHY.BODY_SMALL, 
                                    color: COLORS.PRIMARY,
                                    fontWeight: 500,
                                    lineHeight: 1.2
                                }}>
                                    {project?.additionalTime || "Not specified"}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Engineer Selection */}
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ 
                            ...TYPOGRAPHY.BODY_LARGE, 
                            color: COLORS.TEXT,
                            mb: 2 
                        }}>
                            Select Engineer
                        </Typography>
                        <Autocomplete
                            options={engineers}
                            value={selectedEngineer}
                            onChange={(event, newValue) => handleEngineerSelect(newValue)}
                            onInputChange={(event, value) => setSearchTerm(value)}
                            getOptionLabel={(option) => 
                                `${option.name}${option.email ? ` (${option.email})` : ''}`
                            }
                            isOptionEqualToValue={(option, value) => 
                                option._id === value._id
                            }
                            loading={loading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search engineer by name or email"
                                    size="medium"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <Icon name="search" color={COLORS.LIGHT_SECONDARY} size={20} />
                                        ),
                                        endAdornment: (
                                            <>
                                                {loading && <CircularProgress color="inherit" size={20} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            bgcolor: '#fff',
                                        }
                                    }}
                                />
                            )}
                            noOptionsText={
                                loading ? "Loading..." : "No engineers found"
                            }
                        />
                    </Box>

                    {/* Date Selection */}
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ 
                            ...TYPOGRAPHY.BODY_LARGE, 
                            color: COLORS.TEXT,
                            mb: 2 
                        }}>
                            Select Visit Date
                        </Typography>
                        <DatePicker
                            value={visitDate}
                            onChange={handleDateChange}
                            shouldDisableDate={shouldDisableDate}
                            disabled={!selectedEngineer}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    size="medium"
                                    placeholder="Select visit date"
                                    helperText={getDatePickerHelperText()}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                        }
                                    }}
                                />
                            )}
                        />
                    </Box>

                    {/* Time Selection */}
                    {selectedEngineer && visitDate && (
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ 
                                ...TYPOGRAPHY.BODY_LARGE, 
                                color: COLORS.TEXT,
                                mb: 2 
                            }}>
                                Select Visit Time
                                {loadingSlots && (
                                    <CircularProgress size={16} sx={{ ml: 2 }} />
                                )}
                            </Typography>
                            
                            <CustomTimePicker
                                value={visitTime}
                                onChange={setVisitTime}
                                label="Choose visit time (1 hour duration)"
                                disabled={!selectedEngineer || !visitDate}
                                bookedSlots={bookedSlots.filter(slot => 
                                    isSameDay(new Date(slot.startTime), visitDate)
                                )}
                                selectedEngineer={selectedEngineer}
                                selectedDate={visitDate}
                            />
                            
                            {visitTime && (
                                <Typography sx={{ 
                                    ...TYPOGRAPHY.BODY_SMALL, 
                                    color: COLORS.SECONDARY,
                                    mt: 1,
                                    textAlign: 'center'
                                }}>
                                    Selected: {format(visitTime, 'hh:mm a')} to {format(addHours(visitTime, 1), 'hh:mm a')}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Current Bookings Display - Only for selected date */}
                    {selectedEngineer && bookedSlots.length > 0 && visitDate && (
                        <Box sx={{ mb: 3, maxHeight: '250px', overflow: 'hidden' }}>
                            <Typography sx={{ 
                                ...TYPOGRAPHY.BODY_LARGE, 
                                color: COLORS.TEXT,
                                mb: 2 
                            }}>
                                Engineer's Bookings on {format(visitDate, 'MMM dd, yyyy')}
                            </Typography>
                            
                            {/* Filter slots for selected date */}
                            {(() => {
                                const filteredSlots = bookedSlots.filter(slot => 
                                    isSameDay(new Date(slot.startTime), visitDate)
                                );
                                
                                if (filteredSlots.length === 0) {
                                    return (
                                        <Alert 
                                            severity="info" 
                                            sx={{ 
                                                borderRadius: 2,
                                                bgcolor: `${COLORS.SECONDARY}15`,
                                                color: COLORS.SECONDARY,
                                                maxHeight: '80px'
                                            }}
                                        >
                                            No bookings scheduled for this date
                                        </Alert>
                                    );
                                }
                                
                                return (
                                    <Box sx={{ 
                                        maxHeight: '200px',
                                        overflow: 'auto',
                                        '&::-webkit-scrollbar': {
                                            width: '6px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: COLORS.BACKGROUND,
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: COLORS.LIGHT_SECONDARY,
                                            borderRadius: '3px',
                                        }
                                    }}>
                                        <List sx={{ 
                                            bgcolor: COLORS.BACKGROUND,
                                            borderRadius: 2,
                                            border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                                            p: 0
                                        }}>
                                            {filteredSlots
                                                .slice(0, 5) // Show up to 5 slots
                                                .map((slot, index) => {
                                                    const startTime = new Date(slot.startTime);
                                                    const endTime = new Date(slot.endTime);
                                                    
                                                    return (
                                                        <ListItem 
                                                            key={index}
                                                            divider={index < Math.min(filteredSlots.length - 1, 4)}
                                                            sx={{ py: 1.5 }}
                                                        >
                                                            <ListItemText
                                                                primary={
                                                                    <Typography sx={{ 
                                                                        ...TYPOGRAPHY.BODY_STANDARD, 
                                                                        color: COLORS.PRIMARY,
                                                                        fontWeight: 500,
                                                                        lineHeight: 1.2
                                                                    }}>
                                                                        {format(startTime, 'hh:mm a')} - {format(endTime, 'hh:mm a')}
                                                                    </Typography>
                                                                }
                                                                secondary={
                                                                    <Typography sx={{ 
                                                                        ...TYPOGRAPHY.CAPTION, 
                                                                        color: COLORS.TEXT,
                                                                        lineHeight: 1.2
                                                                    }}>
                                                                        {slot.projectName}
                                                                    </Typography>
                                                                }
                                                                sx={{ 
                                                                    '& .MuiListItemText-primary': {
                                                                        mb: 0.5
                                                                    }
                                                                }}
                                                            />
                                                            <Chip
                                                                label={slot.status}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: slot.status === 'BOOKED' ? `${COLORS.ERROR}20` : `${COLORS.SECONDARY}20`,
                                                                    color: slot.status === 'BOOKED' ? COLORS.ERROR : COLORS.SECONDARY,
                                                                    fontWeight: 500,
                                                                    textTransform: 'uppercase',
                                                                    height: '24px'
                                                                }}
                                                            />
                                                        </ListItem>
                                                    );
                                                })}
                                        </List>
                                    </Box>
                                );
                            })()}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ 
                    p: 3, 
                    pt: 2, 
                    gap: 2,
                    borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                }}>
                    {/* Remove Assignment Button */}
                    {isEditMode && project?.assignedEngineer && (
                        <Button
                            onClick={handleClearAssignment}
                            disabled={loading}
                            startIcon={<Icon name="close2" color={COLORS.ERROR} size={18} />}
                            sx={{
                                color: COLORS.ERROR,
                                px: 3,
                                borderRadius: 2,
                                textTransform: 'none',
                                border: `1px solid ${COLORS.ERROR}30`,
                                '&:hover': {
                                    bgcolor: `${COLORS.ERROR}10`,
                                    borderColor: COLORS.ERROR
                                }
                            }}
                        >
                            Remove Assignment
                        </Button>
                    )}

                    <Box sx={{ flex: 1 }} />

                    <Button
                        onClick={onClose}
                        sx={{
                            color: COLORS.TEXT,
                            px: 3,
                            borderRadius: 2,
                            textTransform: 'none',
                            border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.04)',
                                borderColor: COLORS.SECONDARY
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    
                    <Button
                        onClick={handleAssign}
                        disabled={loading || !selectedEngineer || !visitDate || !visitTime}
                        variant="contained"
                        sx={{
                            bgcolor: COLORS.SECONDARY,
                            color: "#fff",
                            px: 3,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: COLORS.PRIMARY,
                            },
                            '&:disabled': {
                                bgcolor: COLORS.LIGHT_SECONDARY,
                            }
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            isEditMode ? "Update Assignment" : "Assign Engineer"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
            
            <AppToast
                open={toast.open}
                type={toast.type}
                title={toast.title}
                message={toast.message}
                onClose={handleCloseToast}
                autoHideDuration={5000}
            />
        </LocalizationProvider>
    );
};

export default AssignEngineerModal;